import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import DashNav from "@/components/chalann/DashNav";
import { listProductsNeedingPricing, bulkUpdatePrices, formatMXN, getStore } from "@/lib/api";
import { Check, ChevronRight, Keyboard, Save, ArrowLeft, Info } from "lucide-react";
import { toast, Toaster } from "sonner";

/**
 * Manual initial pricing flow. Voice is INTENTIONALLY DISABLED here — this is
 * a keyboard-first onboarding screen. Voice-based price updates happen later
 * in PriceDictationModal after the store already has base prices.
 */
export default function PreciosIniciales() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const store = getStore();
  const [category, setCategory] = useState(params.get("category") || "");
  const [data, setData] = useState({ total: 0, items: [], facets: [] });
  const [prices, setPrices] = useState({}); // { productId: {sale_price, cost_price?, stock_quantity?} }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showCost, setShowCost] = useState(false);
  const [showStock, setShowStock] = useState(false);

  useEffect(() => {
    if (!store?.id) return nav("/setup");
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, store?.id]);

  const load = async () => {
    setLoading(true);
    try {
      const d = await listProductsNeedingPricing(category || undefined);
      setData(d);
      setPrices({});
    } catch {
      toast.error("No se pudo cargar la lista");
    } finally {
      setLoading(false);
    }
  };

  const setField = (id, field, value) => {
    setPrices((p) => ({
      ...p,
      [id]: { ...(p[id] || {}), [field]: value },
    }));
  };

  const withValues = useMemo(
    () => Object.entries(prices).filter(([, v]) => v.sale_price !== undefined && v.sale_price !== "" && Number(v.sale_price) > 0),
    [prices]
  );

  const save = async () => {
    if (withValues.length === 0) return toast.error("Escribe al menos un precio");
    setSaving(true);
    try {
      const items = withValues.map(([id, v]) => ({
        id,
        sale_price: Number(v.sale_price),
        cost_price: v.cost_price !== undefined && v.cost_price !== "" ? Number(v.cost_price) : undefined,
        stock_quantity: v.stock_quantity !== undefined && v.stock_quantity !== "" ? Number(v.stock_quantity) : undefined,
      }));
      const r = await bulkUpdatePrices(items);
      toast.success(`${r.updated} ${r.updated === 1 ? "precio guardado" : "precios guardados"}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudieron guardar los precios");
    } finally {
      setSaving(false);
    }
  };

  const focusNext = (e, currentIndex) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const next = document.querySelector(`[data-testid="price-input-${currentIndex + 1}"]`);
    if (next) next.focus();
  };

  return (
    <main className="min-h-screen bg-[#050505] text-white" data-testid="precios-iniciales">
      <DashNav />
      <div className="grain-overlay" />
      <Toaster position="top-center" theme="dark" richColors />

      <div className="mx-auto max-w-6xl px-6 md:px-10 pt-14 pb-40 relative z-10">
        {/* Header */}
        <div className="flex items-start justify-between gap-6 mb-8">
          <div>
            <button
              onClick={() => nav("/inventario")}
              className="flex items-center gap-1.5 text-white/50 hover:text-white text-[10px] font-mono uppercase tracking-widest mb-4"
              data-testid="back-to-inventario"
            >
              <ArrowLeft size={12} /> Regresar a inventario
            </button>
            <p className="font-mono text-[10px] tracking-widest text-white/40 uppercase mb-2">
              / Captura de Precios Iniciales
            </p>
            <h1 className="font-satoshi text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
              Ponle precio a lo que <span className="text-[#CCFF00]">tú vendes.</span>
            </h1>
            <p className="text-white/60 mt-4 max-w-2xl text-base">
              Ya te precargamos los productos. Ahora sólo escribe el precio con el que tú los vendes.{" "}
              <span className="text-white/40">
                Este primer paso es a mano. Después podrás cambiar precios hablando con Chalán.
              </span>
            </p>
          </div>
          {/* Progress badge */}
          <div className="hidden md:flex flex-col items-end gap-1 shrink-0">
            <span className="font-mono text-[10px] tracking-widest text-white/40 uppercase">Faltan</span>
            <span className="font-satoshi text-4xl text-[#FF5A00] leading-none" data-testid="pending-count">
              {data.total}
            </span>
            <span className="font-mono text-[10px] tracking-widest text-white/40 uppercase">sin precio</span>
          </div>
        </div>

        {/* Voice-off notice */}
        <div className="mb-8 rounded-2xl border border-white/10 bg-white/[0.02] px-5 py-4 flex items-start gap-3">
          <Keyboard size={18} className="text-[#CCFF00] shrink-0 mt-0.5" />
          <div className="text-sm text-white/70">
            <strong className="text-white">Este paso se captura con el teclado.</strong> Nada por voz.
            Te aseguramos que quede exactamente como tú lo vendes. Después de esto ya podrás
            decir <em className="text-[#CCFF00]">&quot;Chalán, el mango subió a 40 el kilo&quot;</em> y se actualiza al instante.
          </div>
        </div>

        {/* Category filter */}
        {data.facets.length > 0 && (
          <div className="mb-6 flex flex-wrap items-center gap-2">
            <span className="font-mono text-[10px] tracking-widest text-white/40 uppercase mr-2">Categoría:</span>
            <button
              onClick={() => setCategory("")}
              className={`px-3 py-1.5 rounded-full text-[11px] font-mono uppercase tracking-widest border transition ${
                !category
                  ? "bg-white text-black border-white"
                  : "border-white/15 text-white/60 hover:text-white"
              }`}
              data-testid="pricing-filter-all"
            >
              Todas · {data.facets.reduce((a, b) => a + b.count, 0)}
            </button>
            {data.facets.map((f) => (
              <button
                key={f.category}
                onClick={() => setCategory(f.category)}
                className={`px-3 py-1.5 rounded-full text-[11px] font-mono uppercase tracking-widest border transition ${
                  category === f.category
                    ? "bg-white text-black border-white"
                    : "border-white/15 text-white/60 hover:text-white"
                }`}
                data-testid={`pricing-filter-${f.category}`}
              >
                {f.category} · {f.count}
              </button>
            ))}
          </div>
        )}

        {/* Optional columns toggle */}
        <div className="mb-4 flex items-center gap-4 text-[11px] font-mono uppercase tracking-widest">
          <label className="flex items-center gap-2 cursor-pointer text-white/60 hover:text-white">
            <input
              type="checkbox"
              checked={showCost}
              onChange={(e) => setShowCost(e.target.checked)}
              className="accent-[#CCFF00]"
              data-testid="toggle-cost"
            />
            + Precio de compra
          </label>
          <label className="flex items-center gap-2 cursor-pointer text-white/60 hover:text-white">
            <input
              type="checkbox"
              checked={showStock}
              onChange={(e) => setShowStock(e.target.checked)}
              className="accent-[#CCFF00]"
              data-testid="toggle-stock"
            />
            + Existencia inicial
          </label>
        </div>

        {/* Empty state */}
        {!loading && data.items.length === 0 && (
          <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-12 text-center">
            <Check size={40} className="mx-auto mb-4 text-[#CCFF00]" />
            <h2 className="font-satoshi text-2xl mb-2">Todos tus productos ya tienen precio</h2>
            <p className="text-white/50 text-sm mb-6">
              De aquí en adelante puedes cambiarlos con la voz. Prueba diciendo{" "}
              <em className="text-white/70">&quot;Chalán, el jitomate subió a 35 el kilo&quot;</em>.
            </p>
            <button
              onClick={() => nav("/inventario")}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#CCFF00] text-black font-mono text-xs uppercase tracking-widest"
              data-testid="go-inventario-done"
            >
              Ir al inventario <ChevronRight size={14} />
            </button>
          </div>
        )}

        {/* Table */}
        {data.items.length > 0 && (
          <div className="rounded-3xl border border-white/10 overflow-hidden bg-white/[0.02]" data-testid="pricing-table">
            {/* Head */}
            <div
              className="grid gap-3 px-5 py-3 border-b border-white/10 bg-white/[0.03] font-mono text-[10px] tracking-widest uppercase text-white/40"
              style={{
                gridTemplateColumns: `1fr 90px 110px${showCost ? " 110px" : ""}${showStock ? " 110px" : ""}`,
              }}
            >
              <span>Producto</span>
              <span className="text-right">Unidad</span>
              <span className="text-right">Precio venta</span>
              {showCost && <span className="text-right">Costo</span>}
              {showStock && <span className="text-right">Existencia</span>}
            </div>
            {/* Rows */}
            <div className="divide-y divide-white/5">
              {data.items.map((p, i) => {
                const values = prices[p.id] || {};
                const done = values.sale_price && Number(values.sale_price) > 0;
                return (
                  <motion.div
                    key={p.id}
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: Math.min(i * 0.008, 0.4) }}
                    className={`grid gap-3 px-5 py-3 items-center ${done ? "bg-[#CCFF00]/[0.03]" : ""}`}
                    style={{
                      gridTemplateColumns: `1fr 90px 110px${showCost ? " 110px" : ""}${showStock ? " 110px" : ""}`,
                    }}
                    data-testid={`pricing-row-${i}`}
                  >
                    <div className="min-w-0">
                      <p className="font-satoshi text-sm truncate" data-testid={`pricing-name-${i}`}>
                        {p.name}
                      </p>
                      <p className="font-mono text-[9px] tracking-widest text-white/40 uppercase">
                        {p.category} {p.brand ? `· ${p.brand}` : ""}
                      </p>
                    </div>
                    <p className="text-right font-mono text-[10px] tracking-widest text-white/50 uppercase">
                      {p.unit || "pza"}
                    </p>
                    <div className="relative">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-white/40 text-xs">$</span>
                      <input
                        type="number"
                        inputMode="decimal"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        value={values.sale_price ?? ""}
                        onChange={(e) => setField(p.id, "sale_price", e.target.value)}
                        onKeyDown={(e) => focusNext(e, i)}
                        className={`w-full pl-6 pr-2 py-2 rounded-lg border text-right font-satoshi text-sm bg-black/40 focus:outline-none focus:ring-1 ${
                          done
                            ? "border-[#CCFF00]/40 text-[#CCFF00] focus:ring-[#CCFF00]"
                            : "border-white/10 text-white focus:ring-white/40"
                        }`}
                        data-testid={`price-input-${i}`}
                      />
                    </div>
                    {showCost && (
                      <div className="relative">
                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-white/40 text-xs">$</span>
                        <input
                          type="number"
                          inputMode="decimal"
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                          value={values.cost_price ?? ""}
                          onChange={(e) => setField(p.id, "cost_price", e.target.value)}
                          className="w-full pl-6 pr-2 py-2 rounded-lg border border-white/10 text-right font-satoshi text-sm bg-black/40 focus:outline-none focus:ring-1 focus:ring-white/40"
                          data-testid={`cost-input-${i}`}
                        />
                      </div>
                    )}
                    {showStock && (
                      <input
                        type="number"
                        inputMode="decimal"
                        step="1"
                        min="0"
                        placeholder="0"
                        value={values.stock_quantity ?? ""}
                        onChange={(e) => setField(p.id, "stock_quantity", e.target.value)}
                        className="w-full px-2 py-2 rounded-lg border border-white/10 text-right font-satoshi text-sm bg-black/40 focus:outline-none focus:ring-1 focus:ring-white/40"
                        data-testid={`stock-input-${i}`}
                      />
                    )}
                  </motion.div>
                );
              })}
            </div>
          </div>
        )}

        {loading && (
          <div className="text-center py-16 text-white/40 font-mono text-xs uppercase tracking-widest">
            Cargando productos sin precio…
          </div>
        )}
      </div>

      {/* Sticky save bar */}
      {data.items.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 border-t border-white/10 bg-black/85 backdrop-blur-lg z-40" data-testid="pricing-savebar">
          <div className="mx-auto max-w-6xl px-6 md:px-10 py-4 flex items-center justify-between gap-4">
            <div className="text-sm">
              <span className="font-satoshi text-2xl text-white" data-testid="pricing-ready-count">
                {withValues.length}
              </span>
              <span className="text-white/50 ml-2 font-mono text-[10px] uppercase tracking-widest">
                {withValues.length === 1 ? "producto listo para guardar" : "productos listos para guardar"}
              </span>
              {withValues.length > 0 && (
                <span className="ml-3 font-mono text-[11px] text-[#CCFF00]">
                  Total: {formatMXN(withValues.reduce((sum, [, v]) => sum + (Number(v.sale_price) || 0), 0))}
                </span>
              )}
            </div>
            <button
              onClick={save}
              disabled={saving || withValues.length === 0}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#CCFF00] text-black font-mono text-xs uppercase tracking-widest disabled:opacity-40"
              data-testid="save-prices-btn"
            >
              <Save size={14} /> {saving ? "Guardando…" : "Guardar precios"}
            </button>
          </div>
        </div>
      )}
    </main>
  );
}
